/**
 * Mensagem legível de um erro de API.
 *
 * Existe porque o backend deste módulo faz questão de escrever motivos em
 * português e NOMEAR o recurso quebrado ("A ocasião 'X' usa o campo de data
 * 'Y', que não é permitido"). Uma tela que mostra texto genérico no lugar joga
 * fora a única informação que leva quem opera até o conserto — e o custo disso
 * é um chamado aberto para uma configuração que a própria mensagem explicava.
 *
 * O fallback existe para o que não tem motivo legível: queda de rede, 500 sem
 * corpo, timeout. Nesses casos o texto genérico é honesto.
 */
export function mensagemDeErro(erro: unknown, padrao: string): string {
  const detalhe = (erro as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;

  if (typeof detalhe === "string" && detalhe.trim()) {
    return detalhe;
  }

  // FastAPI devolve lista de erros de validação; a primeira mensagem é a útil.
  if (Array.isArray(detalhe)) {
    const primeira = detalhe.find(
      (item) => typeof (item as { msg?: unknown })?.msg === "string"
    ) as { msg?: string } | undefined;
    if (primeira?.msg) return primeira.msg;
  }

  return padrao;
}

/** Os status em que o backend escreve a recusa à mão, em português, para quem usa a tela. */
const STATUS_DE_RECUSA = [400, 403, 409, 422];

/**
 * O motivo da recusa escrito pelo servidor, ou o `padrao`.
 *
 * Só vale quando o status é de recusa (400/403/409/422) E o `detail` é texto não vazio. O resto
 * não serve a quem usa a tela e cai no `padrao`: o 404 e o 500 do FastAPI vêm em inglês
 * ("Document not found", "Internal server error"), o 403 do middleware é um código
 * ("password_change_required") e o 422 de validação é uma lista, não texto.
 *
 * Mais estrito que `mensagemDeErro`, que aceita qualquer status e a 1ª `msg` de uma lista.
 */
export function motivoDaRecusa(erro: unknown, padrao: string): string {
  const status = statusDoErro(erro);
  const detalhe = (erro as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  const eRecusa = status !== null && STATUS_DE_RECUSA.includes(status);
  return eRecusa && typeof detalhe === "string" && detalhe.trim() ? detalhe : padrao;
}

/** O status HTTP de um erro do axios (404, 403…), ou ``null`` quando não houve resposta (rede, timeout). */
export function statusDoErro(erro: unknown): number | null {
  const status = (erro as { response?: { status?: unknown } } | null | undefined)?.response?.status;
  return typeof status === "number" ? status : null;
}
